import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  footer?: ReactNode;
  closeOnBackdropClick?: boolean;
  allowDismiss?: boolean;
}

export function Sheet({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = 'lg',
  footer,
  closeOnBackdropClick = true,
  allowDismiss = true,
}: SheetProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && allowDismiss) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, allowDismiss]);

  if (!isOpen) return null;

  const sizes = {
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={() => {
          if (allowDismiss && closeOnBackdropClick) onClose();
        }}
        aria-hidden
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        className={cn(
          'relative flex h-full w-full flex-col border-l border-[#2a2a3a] bg-[#1a1a24] shadow-2xl',
          'animate-slide-in-right',
          sizes[size]
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#2a2a3a] px-6 py-4">
          <div className="min-w-0">
            <h2 id="sheet-title" className="text-lg font-semibold text-[#f8fafc]">
              {title}
            </h2>
            {subtitle && <p className="mt-1 truncate text-sm text-[#94a3b8]">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={() => {
              if (allowDismiss) onClose();
            }}
            disabled={!allowDismiss}
            className="shrink-0 rounded-lg p-1 text-[#64748b] transition-colors hover:bg-[#252535] hover:text-[#f8fafc] disabled:pointer-events-none disabled:opacity-40"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-[#2a2a3a] px-6 py-4">
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
}
